import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { geoNaturalEarth1, geoPath, type GeoPermissibleObjects } from "d3-geo";
import { useEffect, useMemo, useState } from "react";
import { feature } from "topojson-client";
import type { Topology, GeometryCollection } from "topojson-specification";
import { compact, countryName, flag } from "./format";
import { CENTROIDS, NUMERIC_TO_ALPHA2 } from "./world-geo";

type CountryShape = { id: string; code: string; d: string };

const WIDTH = 960;
const HEIGHT = 470;

/** The two framings: the whole world, or the continent most calls come from. */
const VIEWS = {
  world: {
    label: "Monde",
    // Antarctica is left out of the fit: it would take a fifth of the height for no caller.
    // 179.9, not 180: the projection wraps 180 onto -180, and the fit then only frames
    // the western half of the world.
    bounds: [
      [-170, -56],
      [179.9, 78],
    ],
  },
  europe: {
    label: "Europe",
    bounds: [
      [-12, 35],
      [32, 60],
    ],
  },
} as const;

type ViewKey = keyof typeof VIEWS;

/**
 * The atlas is loaded on first render rather than bundled with the page — 100 KB of
 * coastlines nobody else in the back-office needs.
 */
function useWorld() {
  const [world, setWorld] = useState<Topology | null>(null);
  useEffect(() => {
    let cancelled = false;
    import("world-atlas/countries-110m.json").then((mod) => {
      if (!cancelled) setWorld(mod.default as unknown as Topology);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return world;
}

/**
 * Where the conversations come from. Countries are shaded by volume, each one carries a
 * bubble sized by its count (area, not radius, so twice the calls reads as twice the
 * ink), and a country with somebody on the line right now pulses.
 */
export function WorldMap({
  countries,
  liveCountries,
  isLoading,
}: {
  countries: { country_code: string; calls: number; chats: number }[];
  liveCountries: Map<string, number>;
  isLoading?: boolean;
}) {
  const world = useWorld();
  const [view, setView] = useState<ViewKey>("world");
  const [hover, setHover] = useState<{
    code: string;
    x: number;
    y: number;
  } | null>(null);

  const projection = useMemo(() => {
    const [[x0, y0], [x1, y1]] = VIEWS[view].bounds;
    return geoNaturalEarth1().fitExtent(
      [
        [8, 8],
        [WIDTH - 8, HEIGHT - 8],
      ],
      {
        type: "MultiPoint",
        coordinates: [
          [x0, y0],
          [x1, y1],
          [x0, y1],
          [x1, y0],
          [(x0 + x1) / 2, y0],
          [(x0 + x1) / 2, y1],
        ],
      },
    );
  }, [view]);

  const shapes = useMemo<CountryShape[]>(() => {
    if (!world) return [];
    const path = geoPath(projection);
    const collection = feature(
      world,
      world.objects.countries as GeometryCollection,
    );
    return collection.features
      .filter((f) => f.id !== "010")
      .map((f) => ({
        id: String(f.id ?? (f.properties as { name?: string })?.name),
        code: NUMERIC_TO_ALPHA2[String(f.id)] ?? "",
        d: path(f as GeoPermissibleObjects) ?? "",
      }));
  }, [world, projection]);

  const byCode = useMemo(
    () =>
      new Map(
        countries.map((c) => [c.country_code, c.calls + c.chats] as const),
      ),
    [countries],
  );
  const max = Math.max(1, ...byCode.values());
  const known = countries.filter((c) => CENTROIDS[c.country_code]);
  const unknown = countries
    .filter((c) => !CENTROIDS[c.country_code])
    .reduce((sum, c) => sum + c.calls + c.chats, 0);

  const bubbles = known
    .map((c) => {
      const [x, y] = projection(CENTROIDS[c.country_code]) ?? [0, 0];
      const total = c.calls + c.chats;
      return {
        ...c,
        x,
        y,
        total,
        r: 4 + 22 * Math.sqrt(total / max),
        live: liveCountries.get(c.country_code) ?? 0,
      };
    })
    .sort((a, b) => b.total - a.total);

  // A live country with nothing in the window yet (a call that just started) still pulses.
  const liveOnly = [...liveCountries.entries()]
    .filter(([code]) => !byCode.has(code) && CENTROIDS[code])
    .map(([code, live]) => {
      const [x, y] = projection(CENTROIDS[code]) ?? [0, 0];
      return { code, x, y, live };
    });

  const hovered = hover
    ? {
        ...hover,
        calls: countries.find((c) => c.country_code === hover.code)?.calls ?? 0,
        chats: countries.find((c) => c.country_code === hover.code)?.chats ?? 0,
        live: liveCountries.get(hover.code) ?? 0,
      }
    : null;

  return (
    <Card className="h-full overflow-hidden">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="text-sm">D'où ça vient</CardTitle>
          <p className="text-xs text-muted-foreground">
            Conversations par pays d'origine
            {unknown > 0 ? ` · ${compact(unknown)} sans pays connu` : ""}
          </p>
        </div>
        <div className="flex rounded-md border p-0.5 text-xs">
          {(Object.keys(VIEWS) as ViewKey[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className={cn(
                "rounded px-2 py-1 transition-colors",
                view === key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {VIEWS[key].label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="relative p-0">
        {!world || isLoading ? (
          <div className="px-6 pb-6">
            <Skeleton className="w-full aspect-[960/470]" />
          </div>
        ) : (
          <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            className="w-full h-auto select-none"
            role="img"
            aria-label="Carte des conversations par pays"
            onMouseLeave={() => setHover(null)}
          >
            <defs>
              <radialGradient id="cc-bubble" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="var(--chart-1)" stopOpacity="0.9" />
                <stop
                  offset="100%"
                  stopColor="var(--chart-2)"
                  stopOpacity="0.55"
                />
              </radialGradient>
            </defs>
            <g>
              {shapes.map((s) => {
                const count = byCode.get(s.code) ?? 0;
                return (
                  <path
                    key={s.id}
                    d={s.d}
                    fill={count ? "var(--chart-1)" : "var(--chart-grid)"}
                    fillOpacity={count ? 0.15 + 0.5 * Math.sqrt(count / max) : 1}
                    stroke="var(--border)"
                    strokeWidth={0.5}
                    className="transition-[fill-opacity] duration-500"
                    onMouseMove={(e) => {
                      if (!s.code) return;
                      const box = (
                        e.currentTarget.ownerSVGElement as SVGSVGElement
                      ).getBoundingClientRect();
                      setHover({
                        code: s.code,
                        x: e.clientX - box.left,
                        y: e.clientY - box.top,
                      });
                    }}
                  />
                );
              })}
            </g>
            <g>
              {[...bubbles].reverse().map((b) => (
                <g key={b.country_code} className="pointer-events-none">
                  {b.live > 0 ? <PulseRing x={b.x} y={b.y} r={b.r} /> : null}
                  <circle
                    cx={b.x}
                    cy={b.y}
                    r={b.r}
                    fill="url(#cc-bubble)"
                    stroke="var(--card)"
                    strokeWidth={1.5}
                    className="transition-all duration-700"
                  />
                  {b.r > 13 ? (
                    <text
                      x={b.x}
                      y={b.y + 4}
                      textAnchor="middle"
                      fontSize={11}
                      fontWeight={600}
                      fill="white"
                    >
                      {compact(b.total)}
                    </text>
                  ) : null}
                </g>
              ))}
              {liveOnly.map((l) => (
                <g key={l.code} className="pointer-events-none">
                  <PulseRing x={l.x} y={l.y} r={5} />
                  <circle cx={l.x} cy={l.y} r={4} fill="var(--success)" />
                </g>
              ))}
            </g>
          </svg>
        )}

        {hovered ? (
          <div
            className="pointer-events-none absolute z-10 rounded-md border bg-popover px-3 py-2 text-xs shadow-overlay whitespace-nowrap"
            style={{ left: hovered.x + 12, top: hovered.y + 12 }}
          >
            <div className="mb-1 font-medium">
              {flag(hovered.code)} {countryName(hovered.code)}
            </div>
            <div className="tabular-nums text-muted-foreground">
              {hovered.calls} appel(s) · {hovered.chats} chat(s)
            </div>
            {hovered.live ? (
              <div className="mt-0.5 text-success">
                ● {hovered.live} en cours
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-x-4 gap-y-1 px-6 pb-4 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2.5 rounded-full bg-chart-1" /> Volume sur la
            période
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="relative flex size-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
              <span className="relative inline-flex size-2.5 rounded-full bg-success" />
            </span>
            Conversation en cours
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

/** An SVG ring that grows and fades — SMIL, so it costs no React render per frame. */
function PulseRing({ x, y, r }: { x: number; y: number; r: number }) {
  return (
    <circle
      cx={x}
      cy={y}
      r={r}
      fill="none"
      stroke="var(--success)"
      strokeWidth={2}
    >
      <animate
        attributeName="r"
        from={r}
        to={r + 18}
        dur="1.8s"
        repeatCount="indefinite"
      />
      <animate
        attributeName="stroke-opacity"
        from="0.9"
        to="0"
        dur="1.8s"
        repeatCount="indefinite"
      />
    </circle>
  );
}
